'use client';

import { queryKeys } from '@/lib/query-keys';
import { apiClient } from '@borradh-workspace/api-client';
import type {
  CrmTriggerJob,
  CrmTriggerResult,
  CrmTriggersStatus,
  ImportGranolaNoteInput,
  RunCrmJobInput,
  SendTestSlackDmInput,
  SimulateOnboardingSignalInput,
} from '@borradh-workspace/api-client/types';
import { queryOptions, useMutation, useQuery } from '@tanstack/react-query';

const BASE = 'admin-terminal/crm-triggers';

export const crmTriggersStatusQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.adminTerminal.crmTriggersStatus(),
    queryFn: ({ signal }) =>
      apiClient.get<CrmTriggersStatus>(`${BASE}/status`, { signal }),
    staleTime: 60_000,
    // 404 is the answer on production: the page says so instead of retrying.
    retry: false,
  });

export const useCrmTriggersStatus = () =>
  useQuery(crmTriggersStatusQueryOptions());

/** One trigger request: `POST admin-terminal/crm-triggers/<path>` with its body. */
export type CrmTriggerRequest =
  | { kind: 'job'; job: CrmTriggerJob; input: RunCrmJobInput }
  | { kind: 'granola-import'; input: ImportGranolaNoteInput }
  | { kind: 'onboarding-signal'; input: SimulateOnboardingSignalInput }
  | { kind: 'slack-dm'; input: SendTestSlackDmInput };

const pathOf = (request: CrmTriggerRequest) =>
  request.kind === 'job'
    ? `${BASE}/jobs/${request.job}`
    : `${BASE}/${request.kind}`;

/** Fire one trigger and get the job's own result back, as the API returned it. */
export const useRunCrmTrigger = () => {
  const mutation = useMutation({
    mutationFn: (request: CrmTriggerRequest) =>
      apiClient.post<CrmTriggerResult>(pathOf(request), request.input),
  });
  return {
    run: mutation.mutateAsync,
    isRunning: mutation.isPending,
  };
};
